import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Button, CircularProgress,
    Stack, Avatar, Chip, Divider
} from '@mui/material';
import StarIcon        from '@mui/icons-material/Star';
import StarBorderIcon  from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from '../api/axiosConfig';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

const STAR_COLOR = GOLD;

function StarRating({ value, onChange, size = 32, readonly = false }) {
    const [hovered, setHovered] = useState(0);
    const display = readonly ? value : (hovered || value);
    return (
        <Box sx={{ display: 'flex', gap: 0.3 }}>
            {[1, 2, 3, 4, 5].map(n => (
                <Box key={n}
                     onClick={() => !readonly && onChange && onChange(n)}
                     onMouseEnter={() => !readonly && setHovered(n)}
                     onMouseLeave={() => !readonly && setHovered(0)}
                     sx={{ cursor: readonly ? 'default' : 'pointer', display: 'flex', color: STAR_COLOR }}>
                    {n <= display
                        ? <StarIcon       sx={{ fontSize: size }} />
                        : <StarBorderIcon sx={{ fontSize: size, color: 'rgba(0,0,0,0.2)' }} />}
                </Box>
            ))}
        </Box>
    );
}

/** Single rating card (one target: TOUR_OFFER, GUIDE, or RENTAL_OFFER). */
function RatingCard({ label, icon, targetId, targetType, billId, existingRating, onSubmitted }) {
    const [score,     setScore]     = useState(0);
    const [comment,   setComment]   = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error,     setError]     = useState('');

    if (!targetId) return null;

    if (existingRating) {
        return (
            <Box sx={{ p: 2, bgcolor: 'rgba(74,103,65,0.05)', borderRadius: '12px',
                       border: '1px solid rgba(74,103,65,0.15)' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    {icon}
                    <Typography variant="body2" fontWeight={700} sx={{ color: FOREST }}>
                        {label}
                    </Typography>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: FOREST, ml: 'auto' }} />
                    <Typography variant="caption" sx={{ color: FOREST, fontWeight: 700 }}>Rated</Typography>
                </Stack>
                <StarRating value={existingRating.score} readonly size={22} />
                {existingRating.comment && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', mt: 0.8, fontStyle: 'italic' }}>
                        "{existingRating.comment}"
                    </Typography>
                )}
            </Box>
        );
    }

    const handleSubmit = async () => {
        if (score === 0) { setError('Please select a star rating.'); return; }
        setSubmitting(true); setError('');
        try {
            const res = await axios.post('/api/ratings', {
                billId, targetId, targetType, score, comment: comment.trim() || null,
            });
            onSubmitted(res.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || '';
            setError(msg.includes('Already') ? 'Already submitted.' : 'Failed to submit rating.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 2, bgcolor: PARCHMENT, borderRadius: '12px',
                   border: '1px solid rgba(196,98,45,0.15)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                {icon}
                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                    {label}
                </Typography>
            </Stack>
            <StarRating value={score} onChange={setScore} size={28} />
            <TextField
                size="small" fullWidth multiline rows={2}
                placeholder="Share your thoughts (optional)…"
                value={comment}
                onChange={e => setComment(e.target.value)}
                sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
            />
            {error && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                    {error}
                </Typography>
            )}
            <Button size="small" variant="contained" onClick={handleSubmit}
                    disabled={submitting || score === 0}
                    sx={{ mt: 1.5, borderRadius: '20px', fontWeight: 700,
                          bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' },
                          '&.Mui-disabled': { opacity: 0.4 } }}>
                {submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Submit Rating'}
            </Button>
        </Box>
    );
}

/**
 * RatingWidget — drop-in for BookingDetails.
 *
 * Props:
 *   billId      {string}  the bill UUID
 *   billStatus  {string}  current bill status
 *   sourceType  {string}  'TOUR' | 'RENTAL'
 *   offerId     {string}  tourOffer or rentalOffer UUID
 * */
export default function RatingWidget({ billId, billStatus, sourceType, offerId }) {
    const [myRatings, setMyRatings] = useState(null);   // null = loading
    const [tour,      setTour]      = useState(null);    // for guide rating
    const [ready,     setReady]     = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [ratingsRes] = await Promise.all([
                axios.get(`/api/ratings/my-bill/${billId}`),
            ]);
            setMyRatings(ratingsRes.data || []);

            // For TOUR bookings, also fetch the tour to get guide info
            if (sourceType === 'TOUR') {
                try {
                    const tourRes = await axios.get(`/api/tours/view/bill/${billId}`);
                    setTour(tourRes.data);
                } catch { /* tour may not exist yet — that's fine */ }
            }
        } catch {
            setMyRatings([]);
        } finally {
            setReady(true);
        }
    }, [billId, sourceType]);

    useEffect(() => {
        if (billStatus === 'PAID') fetchData();
        else setReady(true);
    }, [billStatus, fetchData]);

    if (billStatus !== 'PAID' || !ready) return null;

    const rated = (type) => myRatings?.find(r => r.targetType === type) || null;

    const handleSubmitted = (newRating) => {
        setMyRatings(prev => [...(prev || []), newRating]);
    };

    const allRated = sourceType === 'TOUR'
        ? !!(rated('TOUR_OFFER') && (!tour?.guideId || rated('GUIDE')))
        : sourceType === 'BIKE_TRAVEL'
        ? !!rated('BIKE_TRAVEL_OFFER')
        : !!rated('RENTAL_OFFER');

    return (
        <Box sx={{ mt: 3, p: 3, bgcolor: 'white', borderRadius: '16px',
                   border: '1px solid rgba(196,98,45,0.15)',
                   boxShadow: '0 2px 12px rgba(44,31,20,0.06)' }}>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <StarIcon sx={{ color: GOLD }} />
                <Typography variant="h6" fontWeight={800} sx={{ color: WALNUT }}>
                    Rate Your Experience
                </Typography>
                {allRated && (
                    <Chip label="All done!" color="success" size="small"
                          icon={<CheckCircleIcon />} sx={{ ml: 'auto', fontWeight: 700 }} />
                )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your feedback helps other travellers and improves our service.
            </Typography>

            <Stack spacing={1.5}>
                {sourceType === 'TOUR' && (
                    <>
                        <RatingCard
                            label="Rate the tour"
                            icon={<StarIcon sx={{ fontSize: '1rem', color: TERRACOTTA }} />}
                            targetId={offerId}
                            targetType="TOUR_OFFER"
                            billId={billId}
                            existingRating={rated('TOUR_OFFER')}
                            onSubmitted={handleSubmitted}
                        />
                        {tour?.guideId && (
                            <>
                                <Divider sx={{ my: 0.5 }} />
                                <RatingCard
                                    label={`Rate your guide${tour.guideName ? ` — ${tour.guideName}` : ''}`}
                                    icon={
                                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem',
                                                      bgcolor: FOREST }}>
                                            {(tour.guideName || 'G')[0].toUpperCase()}
                                        </Avatar>
                                    }
                                    targetId={tour.guideId}
                                    targetType="GUIDE"
                                    billId={billId}
                                    existingRating={rated('GUIDE')}
                                    onSubmitted={handleSubmitted}
                                />
                            </>
                        )}
                    </>
                )}

                {sourceType === 'RENTAL' && (
                    <RatingCard
                        label="Rate this rental"
                        icon={<StarIcon sx={{ fontSize: '1rem', color: TERRACOTTA }} />}
                        targetId={offerId}
                        targetType="RENTAL_OFFER"
                        billId={billId}
                        existingRating={rated('RENTAL_OFFER')}
                        onSubmitted={handleSubmitted}
                    />
                )}

                {sourceType === 'BIKE_TRAVEL' && (
                    <RatingCard
                        label="Rate this bike travel experience"
                        icon={<StarIcon sx={{ fontSize: '1rem', color: TERRACOTTA }} />}
                        targetId={offerId}
                        targetType="BIKE_TRAVEL_OFFER"
                        billId={billId}
                        existingRating={rated('BIKE_TRAVEL_OFFER')}
                        onSubmitted={handleSubmitted}
                    />
                )}
            </Stack>
        </Box>
    );
}
